import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Shield, Users, Zap, Clock, ChevronRight, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingLayout } from '../components/marketing/MarketingLayout';

const tiers = [
  {
    name: 'Starter',
    employees: 'Up to 250 employees',
    price: '$8',
    priceNote: 'per employee/month',
    minimum: '$2,000/mo minimum',
    features: [
      '5 connectors',
      'JML + Access workflows',
      'Email support',
      'Self-serve onboarding',
      '7-year audit retention'
    ],
    cta: 'Start Pilot',
    ctaLink: '/pilot',
    highlighted: false
  },
  {
    name: 'Growth',
    employees: '251-1000 employees',
    price: '$6',
    priceNote: 'per employee/month',
    minimum: '$3,000/mo minimum',
    features: [
      '15 connectors',
      'All standard workflows',
      'Email + Slack support',
      'Guided onboarding',
      'Custom policy templates'
    ],
    cta: 'Request Demo',
    ctaLink: '/demo/request',
    highlighted: true
  },
  {
    name: 'Enterprise',
    employees: '1000+ employees',
    price: 'Custom',
    priceNote: 'volume pricing',
    minimum: 'Contact for details',
    features: [
      'Unlimited connectors',
      'All workflows + custom',
      'Dedicated support',
      'White-glove onboarding',
      'SSO + SCIM provisioning'
    ],
    cta: 'Contact Sales',
    ctaLink: '/demo/request',
    highlighted: false
  }
];

const included = [
  'All agent types (Identity, Endpoint, SaaS, Security)',
  'Policy studio with simulation mode',
  'Audit trail with configurable retention',
  'Slack and Teams integration',
  'ITSM sync (ServiceNow, Jira)',
  'Evidence packs for compliance',
  'Cost controls and budget limits',
  'Standard SLA (4-hour response)'
];

const costControls = [
  { name: 'Per-workflow budget caps', description: 'Set maximum spend per workflow type' },
  { name: 'Per-agent daily limits', description: 'Control how much each agent can spend daily' },
  { name: 'Cost-per-case tracking', description: 'See exactly what each case costs' },
  { name: 'Automatic alerting at 80%', description: 'Get notified before hitting limits' },
  { name: 'Hard stops at budget limit', description: 'Never exceed your budget' }
];

const addOns = [
  { name: 'Custom connector development', price: '$5,000', note: 'one-time' },
  { name: 'Premium support (1-hour response)', price: '+$500', note: '/month' },
  { name: 'Extended audit retention (10 years)', price: '+$1', note: '/employee/month' },
  { name: 'On-premise deployment', price: 'Contact us', note: '' }
];

const faqs = [
  {
    question: 'How is pricing calculated?',
    answer: 'Pricing is based on the number of employees in your organization, not users or seats. Everyone in your directory is covered regardless of how many interact with Vedaya directly.'
  },
  {
    question: 'What counts as a connector?',
    answer: 'Each integration with an external system (Okta, BambooHR, Slack, etc.) counts as one connector. Multiple instances of the same system (e.g., two Okta tenants) count as separate connectors.'
  },
  {
    question: 'Are there any usage limits?',
    answer: 'No artificial limits on cases, actions, or API calls. You pay per employee, and then usage is included. Cost controls help you manage LLM token costs.'
  },
  {
    question: 'What is the contract term?',
    answer: 'Annual contracts with quarterly payment options. Month-to-month available for Starter tier at a 20% premium.'
  },
  {
    question: 'Can we start with a pilot?',
    answer: 'Yes. Our 14-day pilot program lets you see results before committing. Pilot costs are credited toward your first-year contract.'
  }
];

export const Pricing = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen" data-testid="pricing-page">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Pricing</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              Simple, Predictable Pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
              Per-employee pricing. All agents, workflows, and standard connectors included.
              No seat licenses. No usage surprises.
            </p>
          </div>
        </section>

        {/* Pricing Tiers */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {tiers.map((tier, idx) => (
                <div
                  key={idx}
                  className={`rounded-lg border p-6 ${
                    tier.highlighted
                      ? 'border-primary bg-white ring-1 ring-primary'
                      : 'border-border bg-white'
                  }`}
                >
                  {tier.highlighted && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded mb-3">
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-xl font-semibold text-foreground">{tier.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{tier.employees}</p>

                  <div className="mb-2">
                    <span className="text-3xl font-bold text-foreground">{tier.price}</span>
                    <span className="text-sm text-muted-foreground ml-1">{tier.priceNote}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">{tier.minimum}</p>

                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    asChild
                    variant={tier.highlighted ? 'default' : 'outline'}
                    className="w-full"
                  >
                    <Link to={tier.ctaLink}>
                      {tier.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Included in All */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Included in All Plans</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {included.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-secondary/30 rounded-lg p-4">
                  <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cost Controls */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">Cost Controls Built In</h2>
                <p className="text-muted-foreground mb-6">
                  Every deployment includes spend controls. You decide how much automation can cost.
                  No surprise bills.
                </p>
                <ul className="space-y-4">
                  {costControls.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{item.name}</div>
                        <div className="text-sm text-muted-foreground">{item.description}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-lg border border-border p-6">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                  Example Cost Breakdown
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">LLM token costs</span>
                    <span className="font-medium text-foreground">$52.30</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Connector API costs</span>
                    <span className="font-medium text-foreground">$18.40</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-border text-sm">
                    <span className="font-semibold text-foreground">Total (7 days, 642 cases)</span>
                    <span className="font-bold text-foreground">$70.70</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Average cost per case</span>
                    <span className="text-primary font-semibold">$0.11</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Add-ons */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Optional Add-ons</h2>
            <div className="bg-secondary/30 rounded-lg border border-border overflow-hidden">
              {addOns.map((addon, idx) => (
                <div key={idx} className="flex items-center justify-between px-5 py-4 border-b border-border last:border-0">
                  <span className="text-sm text-foreground">{addon.name}</span>
                  <span className="text-sm font-semibold text-foreground">
                    {addon.price}
                    {addon.note && <span className="font-normal text-muted-foreground ml-1">{addon.note}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Ready to Get Started?</h2>
            <p className="text-muted-foreground mb-8">
              Start with a 14-day pilot. See measurable results before committing.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pilot">View Pilot Program</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Pricing;
